// =======================================
// DATA TYPES
// =======================================

/**
 * Represents a single item in the dropdown list
 */
export interface DropdownItem {
  /** Unique identifier for the item */
  id: string | number;
  /** Display text for the item */
  label: string;
  /** The actual value that will be returned when selected */
  value: any;
  /** Whether this item is disabled and cannot be selected */
  disabled?: boolean;
}

// =======================================
// COMPONENT CONFIGURATION
// =======================================

/**
 * Props interface for the Dropdown component
 */
export interface DropdownProps {
  /** Array of items to display in the dropdown */
  items: DropdownItem[];
  /** Currently selected value (single value or array for multi-select) */
  value: any;
  /** Callback when selection changes (single value or array for multi-select) */
  onChange: (value: any) => void;
  /** Placeholder text when no item is selected */
  placeholder?: string;
  /** Label for the dropdown */
  label?: string;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Whether to enable search functionality */
  searchable?: boolean;
  /** Whether to enable multi-select functionality */
  multiSelect?: boolean;
  /** Custom class name for the dropdown container */
  className?: string;
  /** Whether the dropdown is in an error state */
  error?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /** Whether the dropdown is required */
  required?: boolean;
  /** Whether to hide the dropdown arrow */
  hideArrow?: boolean;
  /** Whether to auto-open the dropdown when mounted */
  autoOpen?: boolean;
  /** Custom render function for dropdown items */
  renderItem?: (item: DropdownItem) => React.ReactNode;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

/**
 * Available dropdown styling variants
 */
export const DROPDOWN_VARIANTS = {
  OUTLINED: 'outlined',
  FILLED: 'filled',
  STANDARD: 'standard',
} as const;

export type DropdownVariant = typeof DROPDOWN_VARIANTS[keyof typeof DROPDOWN_VARIANTS];

// =======================================
// COMPONENT SETTINGS
// =======================================

/**
 * Default configuration values for the dropdown component
 */
export const DEFAULT_DROPDOWN_CONFIG = {
  variant: DROPDOWN_VARIANTS.OUTLINED,
  searchable: false,
  multiSelect: false,
  disabled: false,
  required: false,
  placeholder: 'Select an option',
} as const; 